"use client";

import { RULES_QUERY_KEY } from "@/components/rules/hooks/use-rules";
import { useApiClient } from "@/hooks/use-api-client";
import { useFreeUnlimitedUsage } from "@/hooks/use-feature-flag";
import { DevLogger } from "@/lib/logger";
import type {
	ApiClient,
	ApiMeReponse,
	GetInboxBackfillStatusResponse,
	GetSendAsApiResponse,
	GetSignatureApiResponse,
	GetSubscriptionApiResponse,
	GmailLabel,
	Rule,
} from "@jace/api";
import { ApiClientError, isErr } from "@jace/api";
import type { GetIntegrationsResponse } from "@jace/api/src/types/integrations";
import { updateGleapContact } from "@jace/insights";
import { useQueries } from "@tanstack/react-query";

// TODO: Split this up into multiple files
export const SUBSCRIPTION_KEY = ["subscription"];
export const USERDATA_KEY = ["me"];
export const SIGNATURE_QUERY_KEY = ["signature"];
export const SEND_AS_QUERY_KEY = ["send_as"];
export const INBOX_BACKFILL_STATUS_KEY = ["inbox_backfill_status"];
export const GMAIL_LABELS_QUERY_KEY = ["gmail-labels"] as const;

function getData<T>(url: string) {
	return async (client: ApiClient): Promise<T> => {
		const result = await client.get<T>(url);

		if (isErr(result)) {
			const error = new ApiClientError(
				result.error.message,
				result.error.status,
			);

			DevLogger.error(error, {
				message: "Error occured during AppData fetching",
				url,
			});

			throw error;
		}

		return result.value;
	};
}

export interface ExtendedSubscription extends GetSubscriptionApiResponse {
	lastCheckedAt: string;
	isFreePlan: boolean;
	isPlusPlan: boolean;
	isProPlan: boolean;
	isUnlimitedPlan: boolean;
}

export const getRules = getData<Rule[]>("/rules");
export const getSubscription =
	getData<GetSubscriptionApiResponse>("/user_subscription");
export const getSignature = getData<GetSignatureApiResponse>("/signature");
export const getSendAs = getData<GetSendAsApiResponse>("/send_as");
export const getUserData = getData<ApiMeReponse>("/me");
export const getBackfillStatus = getData<GetInboxBackfillStatusResponse>(
	"/inbox/backfill_status",
);
export const getGmailLabels = getData<GmailLabel[]>("/gmail/labels");
export const getIntegrations =
	getData<GetIntegrationsResponse[]>("/integrations");

export async function getExtendedSubscription(
	client: ApiClient,
	unlimited: boolean,
): Promise<ExtendedSubscription> {
	const data = await getSubscription(client);
	const name = unlimited ? "Unlimited" : data.name;
	// Update Gleap contact info with subscription details
	updateGleapContact({ plan: name });

	return {
		...data,
		name,
		isUnlimitedPlan: unlimited,
		isFreePlan: unlimited ? false : data.name === "Free",
		isPlusPlan: unlimited
			? false
			: data.name === "Plus" || data.name === "Plus Yearly",
		isProPlan: unlimited
			? false
			: data.name === "Pro" || data.name === "Pro Yearly",
		lastCheckedAt: new Date().toISOString(),
	};
}

export function AppDataProvider({
	children,
}: { children: React.ReactNode }): React.ReactElement | null {
	const { client } = useApiClient();
	const unlimited = useFreeUnlimitedUsage();

	const appData = useQueries({
		queries: [
			{
				queryKey: RULES_QUERY_KEY,
				queryFn: async (): Promise<Rule[]> => {
					return await getRules(client);
				},
				retry: (failureCount) => {
					return failureCount < 3;
				},
			},
			{
				queryKey: SUBSCRIPTION_KEY,
				queryFn: async (): Promise<ExtendedSubscription> => {
					return await getExtendedSubscription(client, !!unlimited);
				},
				retry: (failureCount) => {
					return failureCount < 3;
				},
			},
			{
				queryKey: USERDATA_KEY,
				queryFn: async (): Promise<ApiMeReponse> => {
					return await getUserData(client);
				},
				retry: (failureCount) => {
					return failureCount < 3;
				},
			},
			{
				queryKey: SIGNATURE_QUERY_KEY,
				queryFn: async (): Promise<string | null> => {
					return await getSignature(client);
				},
				retry: (failureCount) => {
					return failureCount < 3;
				},
			},
			{
				queryKey: GMAIL_LABELS_QUERY_KEY,
				queryFn: async (): Promise<GmailLabel[]> => {
					return await getGmailLabels(client);
				},
				retry: (failureCount) => {
					return failureCount < 3;
				},
			},
			{
				queryKey: SEND_AS_QUERY_KEY,
				queryFn: async (): Promise<GetSendAsApiResponse> => {
					return await getSendAs(client);
				},
				retry: (failureCount) => {
					return failureCount < 3;
				},
			},
		],
	});

	const appDataErrors = appData.reduce<any[]>((acc, item) => {
		if (item.error) {
			acc.push(item.error);
		}
		return acc;
	}, []);

	if (appDataErrors.length > 0) {
		console.log("Error during app data fetching", appDataErrors);
		return <div>Error during app data fetching</div>;
	}

	const appDataLoading = appData.some((item) => item.isLoading);

	if (appDataLoading) return null;

	return <>{children}</>;
}
